import { useEffect, useRef, useState } from 'react';
import { ImageIcon, Upload, X } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { Button } from '../ui/Button';
import { LOGO_ACCEPT, logoFileProblem } from './logoFile';

const ACCEPT = LOGO_ACCEPT;

/**
 * The tile a logo previews on. Fixed in both themes, like the logo itself: a logo is drawn for a
 * background, and a transparent dark mark on a dark card (or a white one on a white card) would
 * vanish. So the tile, its edge and the empty-slot icon use fixed colours only, never theme tokens.
 */
const BACKDROPS = {
  /** Primary and icon logos, made for light backgrounds. */
  light: { tile: 'border-black/10 bg-white', placeholder: 'text-black/25' },
  /** The dark variant, made for dark backgrounds: the top bar's near-black. */
  dark: { tile: 'border-white/10 bg-[#0E0E10]', placeholder: 'text-white/30' },
} as const;

/**
 * A single logo variant slot: an auth-proxied image preview (fetched as a blob →
 * object URL, like `Avatar`), an Upload control, and a Remove option. Uploading
 * stores the returned proxy URL via `onChange`; removing sets it to null.
 */
export function LogoSlot({
  label,
  value,
  onChange,
  disabled,
  backdrop = 'light',
}: {
  label: string;
  /** Stored proxy URL (`/api/assets/:id/image`) or null. */
  value: string | null;
  onChange: (proxyUrl: string | null) => void;
  disabled?: boolean;
  /** The background this variant is made for, and so the tile it previews on. */
  backdrop?: keyof typeof BACKDROPS;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the stored logo as an authed blob → object URL (best-effort).
  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();

    void api
      .fetchAssetBlob(value, controller.signal)
      .then((blob) => {
        if (cancelled || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        // Preview is best-effort: fall back to the placeholder silently.
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
    };
  }, [value]);

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    // The picker's `accept` is a suggestion ("All files" is one click away), and the upload endpoint
    // now also stores PDFs - so a logo.pdf would be saved as the logo and every design would lose it.
    const problem = logoFileProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const asset = await api.uploadAsset(file);
      onChange(asset.blobUrl);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Upload failed — the API may be unreachable.',
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`flex h-24 w-full items-center justify-center overflow-hidden rounded-lg border ${BACKDROPS[backdrop].tile}`}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`${label} preview`}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <ImageIcon className={`h-7 w-7 ${BACKDROPS[backdrop].placeholder}`} aria-hidden />
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={handleFile}
        disabled={disabled || uploading}
        aria-label={`Upload ${label}`}
      />

      <div className="flex w-full items-center justify-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={uploading}
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-3.5 w-3.5" aria-hidden />
          Upload
        </Button>
        {value && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled || uploading}
            onClick={() => onChange(null)}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Remove
          </Button>
        )}
      </div>

      <span className="text-xs font-medium text-ink-label">{label}</span>
      {error && (
        <p role="alert" className="text-center text-xs text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}
    </div>
  );
}
